import { HangerList } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUserOrganization } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { UserVisibleError } from '../UserVisibleError';
import { receiveMaterialListEditResponse } from './receiveMaterialListEditResponse';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActionTypes';

export const createHangerList =
  (name: string): AsyncThunkActionReturn<string> =>
  async (dispatch) => {
    const guid = crypto.randomUUID();
    await dispatch(upsertHangerListPrototype({ guid, name, isArchived: false, hangerHardware: [] }));

    return guid;
  };

export const upsertHangerListPrototype =
  (updatedPrototype: HangerList): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const organization = getLoggedInUserOrganization(state);

    if (organization == null) throw new Error('Unauthenticated user attempted to edit hanger list.');

    const spinnerId = dispatch(addSpinnerMessage('Saving hanger list...'));
    try {
      const result = await designService.materialLists.upsertHangerList(organization, updatedPrototype);
      dispatch(receiveMaterialListEditResponse(result));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error saving hanger list'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const duplicateHangerList =
  (sourceGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const materialSetPrototype = getState().materialSetPrototype;
    if (materialSetPrototype.status !== 'loaded') {
      throw new UserVisibleError('Hanger lists are not loaded. Unable to duplicate.');
    }

    const sourceList = materialSetPrototype.hangerLists[sourceGuid];
    if (sourceList == null) {
      throw new UserVisibleError('That hanger list does not exist. Unable to duplicate.');
    }

    const duplicatedListGuid = crypto.randomUUID();
    const duplicatedList = {
      ...sourceList,
      guid: duplicatedListGuid,
      name: `Copy of ${sourceList.name}`,
    };

    await dispatch(upsertHangerListPrototype(duplicatedList));
  };

export const renameHangerList =
  (hangerListGuid: string, newName: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to rename hanger list.');

    const response = await designService.materialLists.renameHangerList(organization, hangerListGuid, newName);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const archiveHangerList =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to delete hanger list.');

    const response = await designService.materialLists.archiveHangerList(organization, guid);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const restoreHangerList =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to restore hanger list.');

    const response = await designService.materialLists.restoreHangerList(organization, guid);
    dispatch(receiveMaterialListEditResponse(response));
  };
